<template>
  <Modal
    :open="props.open"
    @close-modal="$emit('close-modal')"
    :custom-height="'h-auto'"
    :custom-width="'!w-[650px]'"
  >
    <template #modal-header>
      <div class="flex flex-col text-strongest">
        <div class="flex gap-2 items-center">
          <Icon name="Link" class="h-4 w-4 text-info" />
          <p class="font-inter text-sm text-info font-semibold">Signing Links</p>
        </div>
        <h3 class="font-semibold text-2xl">{{ document?.name }}</h3>
      </div>
    </template>
    <template #modal-body>
      <div class="p-6 flex flex-col gap-4">
        <!-- Owner Link -->
        <div
          v-if="ownerLink"
          class="bg-container-bg rounded-xl p-4 flex items-center justify-between gap-3"
        >
          <div class="flex flex-col min-w-0">
            <p class="font-inter font-semibold text-sm text-strongest">Owner</p>
            <p class="font-inter text-xs text-strong truncate">{{ ownerLink }}</p>
          </div>
          <Button variant="paper-primary" size="sm" @click="copyLink(ownerLink, 'Owner')">
            <template #icon>
              <Icon
                :name="copiedLabel === 'Owner' ? 'Check' : 'Copy'"
                class="h-4 w-4"
                :class="copiedLabel === 'Owner' ? 'text-success' : ''"
              />
            </template>
          </Button>
        </div>

        <!-- Joint Link -->
        <div
          v-if="jointLink"
          class="bg-container-bg rounded-xl p-4 flex items-center justify-between gap-3"
        >
          <div class="flex flex-col min-w-0">
            <p class="font-inter font-semibold text-sm text-strongest">Joint</p>
            <p class="font-inter text-xs text-strong truncate">{{ jointLink }}</p>
          </div>
          <Button variant="paper-primary" size="sm" @click="copyLink(jointLink, 'Joint')">
            <template #icon>
              <Icon :name="copiedLabel === 'Joint' ? 'Check' : 'Copy'" class="h-4 w-4" />
            </template>
          </Button>
        </div>

        <p v-if="!ownerLink && !jointLink" class="font-inter text-sm text-strong">
          No signing links available for this document yet.
        </p>
      </div>
    </template>
    <template #modal-footer>
      <div class="w-full flex justify-end">
        <Button @click="$emit('close-modal')" variant="paper-primary" size="md">
          <template #label>Close</template>
        </Button>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref } from "vue";
import { useDataStore } from "@/stores";
import { Modal, Icon, Button } from "@/components";
import { extractSigningLinkFromResponse } from "@/utilities";

const dataStore = useDataStore();

const props = defineProps({
  open: { type: Boolean, required: true },
  document: { type: Object, default: null },
});

defineEmits(["close-modal"]);

const copiedLabel = ref(null);

const signers = computed(() => {
  const links = props.document?.record?.signing_links;
  if (!Array.isArray(links)) return [];
  return links.filter((s) => s.role === "signer" && extractSigningLinkFromResponse(s));
});

const ownerSigner = computed(() => {
  return signers.value.find((s) => s.email === dataStore.contact.email);
});

const jointSigner = computed(() => {
  return signers.value.find((s) => s.email !== dataStore.contact.email);
});

const ownerLink = computed(() => extractSigningLinkFromResponse(ownerSigner.value));
const jointLink = computed(() => extractSigningLinkFromResponse(jointSigner.value));

async function copyLink(link, label) {
  try {
    await navigator.clipboard.writeText(link);
    copiedLabel.value = label;
    setTimeout(() => {
      copiedLabel.value = null;
    }, 2000);
  } catch (err) {
    console.error("Failed to copy:", err);
  }
}
</script>
